<script setup lang="ts">
import { onMounted, type Ref } from 'vue';
import Column from 'primevue/column';
import { useRoute, useRouter } from 'vue-router';
import { getI18n } from '@/helpers/i18n';
import { useToast } from 'primevue/usetoast';
import { errToToast } from '@/helpers/tools';
import { useBrowserSessionValue } from '@/helpers/browser_session';
import { useCollection, type SortOption } from '@/composables/managedCollection';
import KpBaseCollectionGrid from '@/components/KpBaseCollectionGrid.vue';
import {
    KorrekturWaermeerzeugerWarmwasser,
    KorrekturWaermeerzeugerWarmwasserCollection,
} from '@/models/KorrekturWaermeerzeugerWarmwasser';

const router = useRouter();
const route = useRoute();
const toast = useToast();

const tableSort: Ref<SortOption[]> = useBrowserSessionValue(`eco2stat-${route.fullPath}-tableSort`, [
    { direction: 'asc', property: 'kombination' },
]);
const pageSize: Ref<number> = useBrowserSessionValue(`eco2stat-${route.fullPath}-pageSize`, 100);
const page: Ref<number> = useBrowserSessionValue(`eco2stat-${route.fullPath}-page`, 1);
const {
    collection,
    serverTotal,
    reload: reloadCollection,
} = useCollection(new KorrekturWaermeerzeugerWarmwasserCollection(), {
    autoload: false,
    tableSort,
    pageSize,
    page,
});

onMounted(() => {
    if (collection.loading.value !== true) {
        collection.query();
    }
});

function editRecord(r: KorrekturWaermeerzeugerWarmwasser) {
    router.push({ name: 'bg_geb_korrektur_waermeerzeuger_warmwasser_edit', params: { id: r?.id } });
}

async function deleteRecord(r: KorrekturWaermeerzeugerWarmwasser) {
    if (window.confirm(getI18n().t('sicher_loeschen'))) {
        try {
            await r.destroy();
            await reloadCollection();
        } catch (e) {
            errToToast(e, toast);
        }
    }
}

function newRecord() {
    router.push({ name: 'bg_geb_korrektur_waermeerzeuger_warmwasser_edit', params: { id: 'new' } });
}
</script>
<template>
    <div style="height: 100%">
        <KpBaseCollectionGrid
            :collection="collection"
            v-model:table-sort="tableSort"
            :title="$t('korr_waermeerzeuger_warmwasser')"
            scrollable
            scroll-height="flex"
            paginator
            v-model:page="page"
            v-model:pageSize="pageSize"
            :total-records="serverTotal"
            @editrecord="editRecord($event as KorrekturWaermeerzeugerWarmwasser)"
            @deleterecord="deleteRecord($event as KorrekturWaermeerzeugerWarmwasser)"
            @new="newRecord"
        >
            <Column field="id" sortable :header="$t('id')" style="width: 5rem"></Column>
            <Column field="kombination" sortable :header="$t('kombination')" style="width: 5rem"></Column>
            <Column field="waermeerzeuger_heizung_1_id" :header="$t('waermeerzeuger_heizung_1')">
                <template #body="slotProps">
                    <div v-if="slotProps.data.waermeerzeuger_heizung_1">
                        {{ slotProps.data.waermeerzeuger_heizung_1 }}
                    </div>
                </template>
            </Column>
            <Column field="waermeerzeuger_heizung_2_id" :header="$t('waermeerzeuger_heizung_2')">
                <template #body="slotProps">
                    <div v-if="slotProps.data.waermeerzeuger_heizung_2">
                        {{ slotProps.data.waermeerzeuger_heizung_2 }}
                    </div>
                </template>
            </Column>
            <Column field="waermeerzeuger_warmwasser_1_id" :header="$t('waermeerzeuger_warmwasser_1')">
                <template #body="slotProps">
                    <div v-if="slotProps.data.waermeerzeuger_warmwasser_1">
                        {{ slotProps.data.waermeerzeuger_warmwasser_1 }}
                    </div>
                </template>
            </Column>
            <Column field="waermeerzeuger_warmwasser_2_id" :header="$t('waermeerzeuger_warmwasser_2')">
                <template #body="slotProps">
                    <div v-if="slotProps.data.waermeerzeuger_warmwasser_2">
                        {{ slotProps.data.waermeerzeuger_warmwasser_2 }}
                    </div>
                </template>
            </Column>
            <Column
                field="korrektur_waermeerzeuger_warmwasser_1_id"
                :header="$t('korrektur_waermeerzeuger_warmwasser_1')"
            >
                <template #body="slotProps">
                    <div v-if="slotProps.data.korrektur_waermeerzeuger_warmwasser_1">
                        {{ slotProps.data.korrektur_waermeerzeuger_warmwasser_1 }}
                    </div>
                </template>
            </Column>
            <Column
                field="korrektur_waermeerzeuger_warmwasser_2_id"
                :header="$t('korrektur_waermeerzeuger_warmwasser_2')"
            >
                <template #body="slotProps">
                    <div v-if="slotProps.data.korrektur_waermeerzeuger_warmwasser_2">
                        {{ slotProps.data.korrektur_waermeerzeuger_warmwasser_2 }}
                    </div>
                </template>
            </Column>
            <Column field="energie_waermequelle_heizung_1_id" :header="$t('energie_waermequelle_heizung_1')">
                <template #body="slotProps">
                    <div v-if="slotProps.data.energie_waermequelle_heizung_1_i18n_key">
                        {{ $t(slotProps.data.energie_waermequelle_heizung_1_i18n_key) }}
                    </div>
                </template>
            </Column>
            <Column field="energie_waermequelle_heizung_2_id" :header="$t('energie_waermequelle_heizung_2')">
                <template #body="slotProps">
                    <div v-if="slotProps.data.energie_waermequelle_heizung_2_i18n_key">
                        {{ $t(slotProps.data.energie_waermequelle_heizung_2_i18n_key) }}
                    </div>
                </template>
            </Column>
            <Column field="energie_waermequelle_warmwasser_1_id" :header="$t('energie_waermequelle_warmwasser_1')">
                <template #body="slotProps">
                    <div v-if="slotProps.data.energie_waermequelle_warmwasser_1_i18n_key">
                        {{ $t(slotProps.data.energie_waermequelle_warmwasser_1_i18n_key) }}
                    </div>
                </template>
            </Column>
            <Column field="energie_waermequelle_warmwasser_2_id" :header="$t('energie_waermequelle_warmwasser_2')">
                <template #body="slotProps">
                    <div v-if="slotProps.data.energie_waermequelle_warmwasser_2_i18n_key">
                        {{ $t(slotProps.data.energie_waermequelle_warmwasser_2_i18n_key) }}
                    </div>
                </template>
            </Column>
            <Column
                field="korrektur_energie_waermequelle_1_id"
                :header="$t('korrektur_energie_waermequelle_warmwasser_1')"
            >
                <template #body="slotProps">
                    <div v-if="slotProps.data.korrektur_energie_waermequelle_1_i18n_key">
                        {{ $t(slotProps.data.korrektur_energie_waermequelle_1_i18n_key) }}
                    </div>
                </template>
            </Column>
            <Column
                field="korrektur_energie_waermequelle_2_id"
                :header="$t('korrektur_energie_waermequelle_warmwasser_2')"
            >
                <template #body="slotProps">
                    <div v-if="slotProps.data.korrektur_energie_waermequelle_2_i18n_key">
                        {{ $t(slotProps.data.korrektur_energie_waermequelle_2_i18n_key) }}
                    </div>
                </template>
            </Column>
        </KpBaseCollectionGrid>
    </div>
</template>
